import { useEffect, useId } from 'react';
import { cx } from './cx.js';

export interface PinPadProps {
  value: string;
  onChange(value: string): void;
  /** Enter key or the submit button, once at least `minLength` digits are in. */
  onSubmit(value: string): void;
  minLength?: number;
  maxLength?: number;
  disabled?: boolean;
  /** Visible heading of the pad (also its accessible name). */
  label?: string;
  submitLabel?: string;
  /** Also take digits, Backspace, Enter and Escape from a physical keyboard. */
  captureKeyboard?: boolean;
  /** Shown under the dots and announced (e.g. "Wrong PIN"). */
  error?: string | undefined;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/** Big-key PIN entry for tills: 4–6 digits, never shows the digits themselves. */
export function PinPad({
  value,
  onChange,
  onSubmit,
  minLength = 4,
  maxLength = 6,
  disabled = false,
  label = 'Enter your PIN',
  submitLabel = 'Sign in',
  captureKeyboard = true,
  error,
}: PinPadProps) {
  const id = useId();
  const canSubmit = !disabled && value.length >= minLength;

  const press = (digit: string) => {
    if (disabled || value.length >= maxLength) return;
    onChange(value + digit);
  };
  const back = () => !disabled && onChange(value.slice(0, -1));
  const clear = () => !disabled && onChange('');
  const submit = () => canSubmit && onSubmit(value);

  useEffect(() => {
    if (!captureKeyboard) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (/^[0-9]$/.test(e.key)) {
        e.preventDefault();
        if (!disabled && value.length < maxLength) onChange(value + e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        if (!disabled) onChange(value.slice(0, -1));
      } else if (e.key === 'Escape') {
        if (!disabled) onChange('');
      } else if (e.key === 'Enter' && target?.tagName !== 'BUTTON') {
        e.preventDefault();
        if (!disabled && value.length >= minLength) onSubmit(value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [captureKeyboard, disabled, maxLength, minLength, onChange, onSubmit, value]);

  return (
    <div
      className={cx('qk-pinpad', disabled && 'qk-pinpad--disabled')}
      role="group"
      aria-labelledby={`${id}-label`}
    >
      <p id={`${id}-label`} className="qk-pinpad__label">
        {label}
      </p>
      <div className="qk-pinpad__dots" aria-hidden="true">
        {Array.from({ length: Math.max(minLength, value.length) }, (_, i) => (
          <span
            key={i}
            className={cx('qk-pinpad__dot', i < value.length && 'qk-pinpad__dot--filled')}
          />
        ))}
      </div>
      <p className="qk-sr-only" aria-live="polite">
        {value.length === 0 ? 'No digits entered' : `${value.length} digits entered`}
      </p>
      {error ? (
        <p className="qk-pinpad__error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="qk-pinpad__keys">
        {KEYS.map((k) => (
          <button
            key={k}
            type="button"
            className="qk-pinpad__key"
            onClick={() => press(k)}
            disabled={disabled}
          >
            {k}
          </button>
        ))}
        <button
          type="button"
          className="qk-pinpad__key qk-pinpad__key--muted"
          onClick={clear}
          disabled={disabled}
          aria-label="Clear"
        >
          C
        </button>
        <button
          type="button"
          className="qk-pinpad__key"
          onClick={() => press('0')}
          disabled={disabled}
        >
          0
        </button>
        <button
          type="button"
          className="qk-pinpad__key qk-pinpad__key--muted"
          onClick={back}
          disabled={disabled}
          aria-label="Delete last digit"
        >
          ⌫
        </button>
      </div>
      <button
        type="button"
        className="qk-btn qk-btn--primary qk-btn--xl qk-btn--block qk-pinpad__submit"
        onClick={submit}
        disabled={!canSubmit}
      >
        <span className="qk-btn__label">{submitLabel}</span>
      </button>
    </div>
  );
}
